import React, { useState } from 'react';
import { ArrowLeft, ExternalLink, Users, Building2, Calendar, MessageSquare, Settings, BarChart3 } from 'lucide-react';
import { tenants, networkGrowthData } from '../../data/mockData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
interface TenantDetailProps {
  tenantId: string;
  onNavigate: (page: string) => void;
  onSwitchToClub: (tenantId: string) => void;
}
export function TenantDetail({
  tenantId,
  onNavigate,
  onSwitchToClub
}: TenantDetailProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const tenant = tenants.find(t => t.id === tenantId);
  if (!tenant) return <div data-id="element-126">Tenant nicht gefunden</div>;
  const tabs = [{
    id: 'overview',
    label: 'Übersicht'
  }, {
    id: 'members',
    label: 'Mitglieder'
  }, {
    id: 'companies',
    label: 'Unternehmen'
  }, {
    id: 'events',
    label: 'Events'
  }, {
    id: 'settings',
    label: 'Einstellungen'
  }];
  return <div className="space-y-6" data-id="element-127">
      <button onClick={() => onNavigate('tenants')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-128">
        <ArrowLeft size={16} data-id="element-129" />
        Zurück zur Übersicht
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-130">
        <div className="h-4 w-full" style={{
        backgroundColor: tenant.primaryColor
      }} data-id="element-131"></div>
        <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-132">
          <div className="flex items-center gap-5" data-id="element-133">
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl shadow-md border-4 border-white" style={{
            backgroundColor: tenant.primaryColor
          }} data-id="element-134">
              {tenant.logo}
            </div>
            <div data-id="element-135">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-136">
                {tenant.name}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500" data-id="element-137">
                <span data-id="element-138">{tenant.league}</span>
                <span data-id="element-139">•</span>
                <span data-id="element-140">
                  Erstellt am{' '}
                  {new Date(tenant.createdAt).toLocaleDateString('de-DE')}
                </span>
                <span data-id="element-141">•</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-142">
                  Aktiv
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => onSwitchToClub(tenant.id)} className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors shadow-sm w-full md:w-auto justify-center" data-id="element-143">
            Als Club-Admin öffnen
            <ExternalLink size={16} data-id="element-144" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-t border-slate-200 px-6" data-id="element-145">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-146">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content - Overview */}
      {activeTab === 'overview' && <div className="space-y-6" data-id="element-147">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-148">
            {[{
          label: 'Aktive Mitglieder',
          value: tenant.memberCount,
          icon: Users,
          color: 'text-blue-600'
        }, {
          label: 'Unternehmen',
          value: tenant.companyCount,
          icon: Building2,
          color: 'text-indigo-600'
        }, {
          label: 'Events (YTD)',
          value: tenant.eventCount,
          icon: Calendar,
          color: 'text-purple-600'
        }, {
          label: 'Feed Posts',
          value: '1,240',
          icon: MessageSquare,
          color: 'text-pink-600'
        }].map((stat, i) => <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-149">
                <div className="flex items-center justify-between mb-3" data-id="element-150">
                  <div className={`p-2 rounded-lg bg-slate-50 ${stat.color}`} data-id="element-151">
                    <stat.icon size={20} data-id="element-152" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900" data-id="element-153">
                  {stat.value}
                </p>
                <p className="text-sm font-medium text-slate-500 mt-1" data-id="element-154">
                  {stat.label}
                </p>
              </div>)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-155">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-156">
              <div className="flex items-center justify-between mb-2" data-id="element-157">
                <div data-id="element-158">
                  <h3 className="font-bold text-slate-800" data-id="element-159">
                    Netzwerk Wachstum
                  </h3>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-160">
                    Mitglieder & Unternehmen pro Monat
                  </p>
                </div>
                <BarChart3 className="text-slate-400" size={20} data-id="element-161" />
              </div>
              <div className="h-72 mt-4" data-id="element-162">
                <ResponsiveContainer width="100%" height="100%" data-id="element-163">
                  <BarChart data={networkGrowthData[tenant.id] || []} barGap={2} barCategoryGap="20%" data-id="element-164">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} data-id="element-165" />
                    <XAxis dataKey="month" tick={{
                  fontSize: 12,
                  fill: '#94a3b8'
                }} axisLine={{
                  stroke: '#e2e8f0'
                }} tickLine={false} data-id="element-166" />
                    <YAxis tick={{
                  fontSize: 12,
                  fill: '#94a3b8'
                }} axisLine={false} tickLine={false} width={40} data-id="element-167" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#0f172a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  padding: '8px 12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }} itemStyle={{
                  color: '#fff',
                  fontSize: '12px'
                }} labelStyle={{
                  color: '#94a3b8',
                  fontSize: '11px',
                  marginBottom: '4px'
                }} cursor={{
                  fill: 'rgba(16, 185, 129, 0.05)'
                }} data-id="element-168" />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{
                  fontSize: '12px',
                  paddingTop: '12px'
                }} data-id="element-169" />
                    <Bar dataKey="mitglieder" name="Mitglieder" fill="#10b981" radius={[4, 4, 0, 0]} data-id="element-170" />
                    <Bar dataKey="unternehmen" name="Unternehmen" fill="#6366f1" radius={[4, 4, 0, 0]} data-id="element-171" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-172">
              <h3 className="font-bold text-slate-800 mb-6" data-id="element-173">
                Letzte Aktivitäten
              </h3>
              <div className="space-y-5" data-id="element-174">
                {[{
              user: 'Thomas Müller',
              action: 'hat sich registriert',
              time: 'Vor 2 Stunden'
            }, {
              user: 'Alpenbank AG',
              action: 'wurde als Unternehmen hinzugefügt',
              time: 'Gestern'
            }, {
              user: 'Event "Business Lunch"',
              action: 'wurde erstellt',
              time: 'Vor 2 Tagen'
            }, {
              user: 'Sandra Berger',
              action: 'hat das Profil aktualisiert',
              time: 'Vor 3 Tagen'
            }].map((act, i) => <div key={i} className="flex items-start gap-4" data-id="element-175">
                    <div className="w-2 h-2 mt-2 rounded-full bg-emerald-500" data-id="element-176"></div>
                    <div data-id="element-177">
                      <p className="text-sm text-slate-800" data-id="element-178">
                        <span className="font-bold" data-id="element-179">{act.user}</span>{' '}
                        {act.action}
                      </p>
                      <p className="text-xs text-slate-400 mt-1" data-id="element-180">{act.time}</p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>}

      {/* Placeholder for other tabs */}
      {activeTab !== 'overview' && <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center" data-id="element-181">
          <Settings className="mx-auto text-slate-300 mb-4" size={48} data-id="element-182" />
          <h3 className="text-lg font-bold text-slate-700 mb-2" data-id="element-183">
            Bereich in Entwicklung
          </h3>
          <p className="text-slate-500 max-w-md mx-auto" data-id="element-184">
            Die Ansicht für "{tabs.find(t => t.id === activeTab)?.label}" wird
            aktuell noch implementiert.
          </p>
        </div>}
    </div>;
}